import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import {
  DATA_STUDIO_ROW_PAGE_SIZE,
  getDataStudioTableRows,
} from "../services/dataStudio/dataStudioTablesApi";
import type {
  DataStudioProfileColumnWindow,
  DataStudioProfileRow,
  DataStudioProfileRowPageInfo,
  GetDataStudioTableRowsResponse,
} from "../services/dataStudio/dataStudioTables.types";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import { useDebouncedValue } from "./useDebouncedValue";
import type { ConnectorAssetIdentity } from "../services/dataStudio/dataStudioAssets.types";

/**
 * Pages evicted from the opposite end once the cache holds this many —
 * bounds memory for a table a user scrolls through at length in either
 * direction without limiting how far they can scroll.
 */
const MAX_CACHED_ROW_PAGES = 5;

/**
 * How long the column window must sit still before the retained row range is
 * refreshed. One rectangular request replaces the cached row pages, avoiding
 * one request per page while a horizontal gesture crosses column boundaries.
 */
const COLUMN_WINDOW_REFETCH_DEBOUNCE_MS = 400;

export interface UseDataStudioTableProfileRowsResult {
  /** Every page received so far, flattened in arrival order. */
  rows: DataStudioProfileRow[];
  /**
   * Paging state of the currently visible rows: `hasPrevious`/`previousOffset`
   * come from the first cached page, `hasMore`/`nextOffset` from the last —
   * either end can be the one still connected to the collection's edge once
   * pages start getting evicted from the opposite end.
   */
  rowPageInfo: DataStudioProfileRowPageInfo | undefined;
  /** Positional column identity from the latest page, for a header-free first paint. */
  columnOrder: GetDataStudioTableRowsResponse["columnOrder"];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  /** True while one rectangular request aligns retained rows to new columns. */
  isRefreshingColumns: boolean;
  /** True while an older row page is in flight. */
  isLoadingOlderRows: boolean;
  /** True while another older page remains to be fetched. */
  hasOlderRows: boolean;
  /** Backend's own reason when a page request fails, else null. */
  errorMessage: string | null;
  /** Requests the next (newer) page; ignored while one is already in flight. */
  loadMore: () => void;
  /** Requests the previous (older) page; ignored while one is already in flight. */
  loadOlder: () => void;
}

/**
 * Offset-paged preview rows for the profile grid. The grid reveals newer rows
 * in small batches from what it already has and calls `loadMore` when it runs
 * out, so scrolling down keeps working past the first page without a
 * page-size cap. Scrolling up near the top calls `loadOlder`, which fetches
 * and prepends the previous page directly — there is no local reveal buffer
 * for that direction, unlike the forward one.
 *
 * The rows API pages by `limit`/`offset` — there is no cursor — so the next
 * and previous page params are the offsets the current page's `meta`
 * implies. `null` ends the collection in that direction, which is what stops
 * React Query fetching further. `maxPages` caps the cache so scrolling far in
 * one direction evicts pages from the opposite end instead of growing
 * unboundedly.
 *
 * `columnWindow` mirrors the columns endpoint's own loaded window
 * (`useDataStudioTableProfileColumns`) onto every row page's `column_offset`/
 * `column_limit`, so a row's cells cover the columns the grid actually has
 * rendered — without it the rows endpoint applies its own column default
 * (offset 0, limit 50) regardless of how far the grid has scrolled
 * horizontally, and columns past that default render blank. `columnWindow`
 * is deliberately left out of the query key: after horizontal scrolling
 * settles, one rectangular request refreshes the retained row range and
 * replaces its individual cached pages. This preserves the vertical range
 * without replaying one request for every cached row page.
 *
 * The query stays `enabled: false` until `columnWindow` is non-null — rows
 * are meaningless before the columns hook has resolved its first page (there
 * is nothing yet to mirror), and firing anyway would send no `column_offset`/
 * `column_limit` at all, land on the backend's own 50-column default, then
 * immediately get corrected by the debounced refetch once the real window
 * arrives. Gating here means the *first* row fetch is already windowed
 * correctly, instead of firing twice for every table opened.
 */
export const useDataStudioTableProfileRows = (
  identity: ConnectorAssetIdentity | null,
  columnWindow: DataStudioProfileColumnWindow | null = null,
): UseDataStudioTableProfileRowsResult => {
  const connectionId = identity?.connectionId ?? "";
  const assetId = identity?.assetId ?? "";
  const liveSampleSlug = identity?.liveSampleSlug ?? null;
  const enabled = Boolean(connectionId && assetId) && columnWindow !== null;
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () =>
      queryKeys.dataStudio.tableProfileRows(
        connectionId || "none",
        assetId || "none",
      ),
    [assetId, connectionId],
  );

  const query = useInfiniteQuery<GetDataStudioTableRowsResponse>({
    queryKey,
    queryFn: ({ pageParam }) =>
      getDataStudioTableRows({
        connectionId,
        assetId,
        liveSampleSlug,
        offset: (pageParam as number) ?? 0,
        limit: DATA_STUDIO_ROW_PAGE_SIZE,
        columnOffset: columnWindow?.offset,
        columnLimit: columnWindow?.limit,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.rowPageInfo.nextOffset,
    getPreviousPageParam: (firstPage) => firstPage.rowPageInfo.previousOffset,
    maxPages: MAX_CACHED_ROW_PAGES,
    // The product resets to the first rows on every sheet switch. Retaining an
    // inactive infinite query only causes all of its stale pages to be replayed
    // when that sheet is selected again.
    gcTime: 0,
    enabled,
  });

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey, exact: true });
    },
    [queryClient, queryKey],
  );

  const columnWindowKey = columnWindow
    ? `${columnWindow.offset}:${columnWindow.limit}`
    : null;
  const debouncedColumnWindowKey = useDebouncedValue(
    columnWindowKey,
    COLUMN_WINDOW_REFETCH_DEBOUNCE_MS,
  );

  /**
   * The column window the currently-cached row pages were last fetched
   * against. Reset synchronously during render (not in an effect) whenever
   * `identity` changes, or whenever the query has just gone from disabled to
   * enabled: both cases mean the row pages currently in the cache (if any)
   * were already fetched — or are about to be fetched for the first time —
   * with the *current* `columnWindow` via `queryFn`'s closure, so there is
   * nothing pending to settle. Resetting here (rather than only comparing
   * debounced values) stops the settle effect below from treating that as a
   * pending change and firing a second, redundant refetch once the debounce
   * catches up. This mutate-during-render is the pattern React documents for
   * resetting derived state when a prop changes.
   */
  const identityKey = `${connectionId}:${assetId}`;
  const previousIdentityKeyRef = useRef(identityKey);
  const previousEnabledRef = useRef(enabled);
  const lastAppliedWindowKeyRef = useRef(columnWindowKey);
  if (
    previousIdentityKeyRef.current !== identityKey ||
    (!previousEnabledRef.current && enabled)
  ) {
    previousIdentityKeyRef.current = identityKey;
    lastAppliedWindowKeyRef.current = columnWindowKey;
  }
  previousEnabledRef.current = enabled;
  const isColumnWindowPending =
    enabled && columnWindowKey !== lastAppliedWindowKeyRef.current;

  useEffect(() => {
    if (!enabled || !columnWindow || !query.data || query.isFetching) return;
    // Still mid-debounce — `columnWindow` has changed again since the timer
    // now firing was scheduled, so wait for the next settle instead of
    // refetching against a window that's already stale.
    if (debouncedColumnWindowKey !== columnWindowKey) return;
    if (debouncedColumnWindowKey === lastAppliedWindowKeyRef.current) return;

    const firstOffset = (query.data.pageParams[0] as number | undefined) ?? 0;
    const retainedRowCount = query.data.pages.reduce(
      (total, page) => total + page.rows.length,
      0,
    );
    if (retainedRowCount === 0) return;

    // Suppress a retry loop if this request fails. React Query owns the request
    // state/error and the next distinct window can still trigger a fresh fetch.
    lastAppliedWindowKeyRef.current = debouncedColumnWindowKey;
    void queryClient
      .fetchInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }) =>
          getDataStudioTableRows({
            connectionId,
            assetId,
            liveSampleSlug,
            offset: (pageParam as number) ?? firstOffset,
            limit: retainedRowCount,
            columnOffset: columnWindow.offset,
            columnLimit: columnWindow.limit,
          }),
        initialPageParam: firstOffset,
        pages: 1,
        getNextPageParam: (lastPage) => lastPage.rowPageInfo.nextOffset,
        maxPages: MAX_CACHED_ROW_PAGES,
        gcTime: 0,
      })
      .catch(() => undefined);
  }, [
    assetId,
    liveSampleSlug,
    columnWindow,
    columnWindowKey,
    connectionId,
    debouncedColumnWindowKey,
    enabled,
    query.data,
    query.isFetching,
    queryClient,
    queryKey,
  ]);

  const rows = useMemo(
    () => query.data?.pages.flatMap((page) => page.rows) ?? [],
    [query.data],
  );

  const firstPage = query.data?.pages[0];
  const lastPage = query.data?.pages[query.data.pages.length - 1];
  const rowPageInfo = useMemo<DataStudioProfileRowPageInfo | undefined>(() => {
    if (!firstPage || !lastPage) return undefined;
    return {
      total: lastPage.rowPageInfo.total,
      nextOffset: lastPage.rowPageInfo.nextOffset,
      hasMore: lastPage.rowPageInfo.hasMore,
      previousOffset: firstPage.rowPageInfo.previousOffset,
      hasPrevious: firstPage.rowPageInfo.hasPrevious,
    };
  }, [firstPage, lastPage]);

  return {
    rows,
    rowPageInfo,
    columnOrder: lastPage?.columnOrder ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    isRefreshingColumns:
      isColumnWindowPending ||
      (query.isRefetching &&
        !query.isFetchingNextPage &&
        !query.isFetchingPreviousPage),
    isLoadingOlderRows: query.isFetchingPreviousPage,
    hasOlderRows: Boolean(query.hasPreviousPage),
    errorMessage: query.isError
      ? getApiErrorMessage(query.error, "Unable to load more preview rows.")
      : null,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetching) {
        query.fetchNextPage();
      }
    },
    loadOlder: () => {
      if (query.hasPreviousPage && !query.isFetching) {
        query.fetchPreviousPage();
      }
    },
  };
};
